/**
 * Leave out, on a setup sample (owner, 2026-10-30): a sample document that does not belong in the job is left out of
 * it. It has no type, so no type counts it or learns from it; it is not junk, so nothing is taught; its file is not
 * imported when the setup is used (the coordinator's stageFolderImport) unless it holds other documents or the sample
 * itself was not read, which the sample then says (leftOutSampleFileStaysIn); and choosing a type for it again puts it
 * back. The proposal keeps it, marked with the type it was left out of, so the sample view of that type still shows
 * it with the choice to put it back (every type's, once that type is gone: leftOutSampleShownUnder). Only the desktop's setup proposal holds the mark: the engine is never sent it,
 * and a Read more that sends the proposal to the engine gets the mark back on its answer by document id.
 */
import type { DocumentResult, ProcessResult } from './desktop-api';
import { folderAnalysisDocumentWasRead } from './folder-analysis-document-read';

/** The sample, left out of the job: no type, the type it was in remembered. */
export const leaveSampleOut = <T extends DocumentResult>(
  document: T,
  typeId: string,
  typeName: string,
): T => ({
  ...document,
  leftOutOfJob: { typeId, typeName },
  documentType: '',
  documentTypeId: null,
  documentTypeVerified: false,
  documentTypeConfidence: 0,
  fields: document.fields.filter((field) => field.name !== 'Document type'),
});

/**
 * Whether a left-out sample shows among a type's samples (`typeIds`: the setup's types now): left out of that type,
 * or of one no longer in the setup (removed or merged away since), which every remaining type's samples then show,
 * so it can still be put back. With no type left it shows nowhere until one is added; it stays left out meanwhile.
 */
export const leftOutSampleShownUnder = (
  document: DocumentResult,
  typeId: string,
  typeIds: ReadonlySet<string>,
): boolean =>
  Boolean(document.leftOutOfJob) &&
  (document.leftOutOfJob!.typeId === typeId || !typeIds.has(document.leftOutOfJob!.typeId));

/** A Read more's answer with the samples left out before it still left out (the engine drops the mark). */
export const keepSamplesLeftOut = (result: ProcessResult, before: ProcessResult): ProcessResult => {
  const leftOut = new Map(
    before.documents
      .filter((document) => document.leftOutOfJob)
      .map((document) => [document.id, document.leftOutOfJob!]),
  );
  if (leftOut.size === 0) return result;
  return {
    ...result,
    documents: result.documents.map((document) => {
      const mark = leftOut.get(document.id);
      return mark && !document.leftOutOfJob
        ? leaveSampleOut(document, mark.typeId, mark.typeName)
        : document;
    }),
  };
};

/**
 * The files a setup's import leaves out: those whose every document was read as a sample and left out (`key` names a
 * file the way the caller compares them; `wholeRead` holds the keys of the files every document of which was read,
 * folder-analysis-evidence.ts's provenFolderAnalysisSources). A file that also holds a sample still in the job, or a
 * document not read, is imported whole, as a file is the smallest thing the import reads.
 */
export const leftOutSampleFiles = (
  documents: readonly DocumentResult[],
  key: (source: string) => string,
  wholeRead: ReadonlySet<string>,
): Set<string> => {
  const kept = new Set<string>(),
    out = new Set<string>();
  for (const document of documents)
    for (const source of sampleSourceKeys(document, key))
      (document.leftOutOfJob ? out : kept).add(source);
  return new Set([...out].filter((source) => !kept.has(source) && wholeRead.has(source)));
};

/** The files (by `key`) a sample document was read from. */
export const sampleSourceKeys = (
  document: DocumentResult,
  key: (source: string) => string,
): Set<string> =>
  new Set(
    [
      ...(document.sourcePaths?.length ? document.sourcePaths : [document.sourcePath]),
      ...document.pages.map((page) => page.sourcePath ?? ''),
    ]
      .filter((source) => source.trim().length > 0)
      .map(key),
  );

/**
 * Why a left-out sample's file is still imported, or null when it isn't: the sample itself was not read, or its file
 * holds other documents (a sample still in the job, or one not read), as the import reads whole files. Read as the
 * coordinator judges a file read whole (folderAnalysisDocumentWasRead). What the renderer can see; the coordinator
 * also keeps a file it did not read whole (leftOutSampleFiles).
 */
export const leftOutSampleFileStaysIn = (
  document: DocumentResult,
  documents: readonly DocumentResult[],
): 'not-read' | 'other-documents' | null => {
  const own = sampleSourceKeys(document, (source) => source.toLowerCase());
  if (own.size === 0) return null;
  if (!folderAnalysisDocumentWasRead(document)) return 'not-read';
  return documents.some(
    (other) =>
      other.id !== document.id &&
      (!other.leftOutOfJob || !folderAnalysisDocumentWasRead(other)) &&
      [...sampleSourceKeys(other, (source) => source.toLowerCase())].some((source) =>
        own.has(source),
      ),
  )
    ? 'other-documents'
    : null;
};
